// apps/client/src/pages/leads/edm/EdmJourneyPage.tsx
//
// /leads/edm/journeys/:journeyId (plan JOURNEYS J8, `edm_journeys`): one
// journey. Trigger, audience, steps (each opens the campaign editor for its
// step template), per-step test status, Activate (disabled until every step
// has a delivered test), Pause / Resume / Archive, and the read-only list of
// the emails (child campaigns) it has sent, with counts. Every rule shown is
// re-checked by the server; reads only through edmJourneyAction.

import React, { useCallback, useEffect, useState } from 'react';
import { Link, Navigate, useOutletContext, useParams } from 'react-router-dom';
import { ArrowLeft, CheckCircle2, CircleAlert, Plus, Route as RouteIcon, Trash2 } from 'lucide-react';
import { PageHeader } from '../../../components/common/PageHeader';
import { Skeleton } from '../../../components/common/SkeletonLoader';
import ConfirmDialog from '../../../components/common/ConfirmDialog';
import { useAuth } from '../../../hooks/useAuth';
import {
  isEdmJourneysEnabled,
  journeyErrorMessage,
  journeyPausedReasonLabel,
  JOURNEY_STATUS_LABEL,
  triggerLabel,
  useEdmJourneys,
  type EdmJourneyDetail,
  type EdmJourneyStats,
} from '../../../hooks/useEdmJourneys';
import { EDM_UI, formatBrisbaneDateTime, isEdmPreferencesEnabled, statusLabel } from '../../../utils/edm';
import { useEdmCampaigns, type EdmSenderCandidate } from '../../../hooks/useEdmCampaigns';
import { useEdmContent } from '../../../hooks/useEdmContent';
import TopicSelect from './components/TopicSelect';

interface OutletCtx {
  userData?: { companyId?: string; role?: string };
  companyData?: { featureFlags?: Record<string, unknown> | null } | null;
}

/** Fixed-rate copy guidance (expiry dates come from the CRM and can be stale). */
export const FIXED_RATE_GUIDANCE =
  'Fixed-rate end dates come from your CRM and can be out of date, so keep the wording soft: "your fixed rate may be ending soon" and an offer to review, never a firm date or a claim about their rate. Start from the Fixed-rate review template.';

/** Whether Activate can be pressed: at least one step and every step tested (server re-checks). */
export function canActivate(j: EdmJourneyDetail | null): boolean {
  return !!j && (j.status === 'draft' || j.status === 'paused') && j.stepDetails.length > 0 && j.stepDetails.every((s) => s.tested && !s.hasAbTest);
}

const audienceLine = (a: EdmJourneyDetail['audience']): string =>
  [a.includeLeads && 'leads', a.includeClients && 'clients', a.includeContacts && 'imported contacts'].filter(Boolean).join(', ') || 'nobody';

const EdmJourneyPage: React.FC = () => {
  const { journeyId } = useParams<{ journeyId: string }>();
  const ctx = useOutletContext<OutletCtx>();
  const { claims } = useAuth();
  const role = ctx?.userData?.role ?? claims?.role;
  const companyId = ctx?.userData?.companyId;
  const enabled = isEdmJourneysEnabled(ctx?.companyData?.featureFlags);
  const api = useEdmJourneys(companyId);
  const campaignApi = useEdmCampaigns(companyId);
  const contentApi = useEdmContent(companyId);
  const preferencesOn = isEdmPreferencesEnabled(ctx?.companyData?.featureFlags);

  const [journey, setJourney] = useState<EdmJourneyDetail | null>(null);
  const [stats, setStats] = useState<EdmJourneyStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [offset, setOffset] = useState('7');
  const [dialog, setDialog] = useState<'archive' | null>(null);

  const load = useCallback(async () => {
    if (!enabled || !journeyId || !companyId) return;
    setLoading(true);
    setError(null);
    try {
      const [j, s] = await Promise.all([api.get(journeyId), api.stats(journeyId)]);
      setJourney(j);
      setStats(s);
    } catch (err) {
      setError(journeyErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [api, companyId, enabled, journeyId]);

  useEffect(() => {
    void load();
  }, [load]);

  const act = async (fn: () => Promise<unknown>, done?: (r: unknown) => string | null) => {
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      const r = await fn();
      setNotice(done ? done(r) : null);
      await load();
    } catch (err) {
      setError(journeyErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  if (!enabled) return <Navigate to="/leads/edm" replace />;
  if (loading && journey === null && error === null) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-10 w-72" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  const id = journeyId as string;
  const status = journey?.status ?? 'draft';
  const structureEditable = status === 'draft' || status === 'paused';
  const resumeNeedsSuperAdmin = !!journey?.pausedReason && /^(reputation|bounce_rate|delivery_feedback_)/.test(journey.pausedReason);
  const activated = (r: unknown) => {
    const n = (r as { counters?: { recipients?: number } })?.counters?.recipients;
    return typeof n === 'number' ? `Journey active. ${n.toLocaleString('en-AU')} people in its audience can be emailed today.` : 'Journey active.';
  };

  const actions = journey && (
    <div className="flex flex-wrap items-center gap-2">
      {status === 'draft' && (
        <button type="button" className={EDM_UI.primaryButton} disabled={busy || !canActivate(journey)} onClick={() => void act(() => api.activate(id), activated)}>
          Activate
        </button>
      )}
      {status === 'paused' && (
        <button
          type="button"
          className={EDM_UI.primaryButton}
          disabled={busy || !canActivate(journey) || (resumeNeedsSuperAdmin && role !== 'super_admin')}
          onClick={() => void act(() => api.resume(id), activated)}
        >
          Resume
        </button>
      )}
      {status === 'active' && (
        <button type="button" className={EDM_UI.secondaryButton} disabled={busy} onClick={() => void act(() => api.pause(id))}>
          Pause
        </button>
      )}
      {status !== 'archived' && (
        <button type="button" className={EDM_UI.dangerButton} disabled={busy} onClick={() => setDialog('archive')}>
          Archive
        </button>
      )}
    </div>
  );

  return (
    <div className="space-y-6">
      <Link to="/leads/edm/journeys" className="inline-flex items-center gap-1.5 text-sm font-medium text-gray-400 hover:text-gray-200">
        <ArrowLeft className="h-4 w-4" /> Back to journeys
      </Link>
      <PageHeader
        icon={RouteIcon}
        title={journey?.name ?? 'Journey'}
        subtitle={journey ? `${JOURNEY_STATUS_LABEL[journey.status]} · ${triggerLabel(journey.trigger)}` : undefined}
        badge={status === 'paused' ? 'Paused' : undefined}
        onRefresh={() => void load()}
        isRefreshing={loading}
        actions={actions}
      />

      {error && <div className={EDM_UI.errorBanner}>{error}</div>}
      {notice && <div className={EDM_UI.infoBanner}>{notice}</div>}
      {journey?.pausedReason && status === 'paused' && <div className={EDM_UI.warnBanner}>{journeyPausedReasonLabel(journey.pausedReason)}</div>}
      {journey?.trigger.type === 'fixed_rate_expiry' && <div className={EDM_UI.infoBanner}>{FIXED_RATE_GUIDANCE}</div>}

      {journey && (
        <>
          <section className={EDM_UI.card} aria-label="Trigger and audience">
            <h2 className={EDM_UI.heading}>Trigger and audience</h2>
            <p className="text-sm text-gray-300">Starts: {triggerLabel(journey.trigger)}.</p>
            <p className="text-sm text-gray-300">Emails: {audienceLine(journey.audience)}.</p>
            <p className="text-xs text-gray-400">
              At most one journey email per person per day. Each email needs consent at the moment it is sent; anyone who unsubscribes leaves every journey.
            </p>
          </section>

          {/* Fix pass X4 (Codex-5): the journey's own topic and sender, so it
              can be activated when the company has topics. Editable while the
              journey is a draft or paused; the server re-checks every rule. */}
          <section className={EDM_UI.card} aria-label="Journey settings">
            <h2 className={EDM_UI.heading}>Settings</h2>
            <JourneySenderSelect
              value={journey.sendAsBrokerUid}
              disabled={busy || !structureEditable}
              loadCandidates={campaignApi.listSenderCandidates}
              onChange={(uid) => void act(() => api.update({ journeyId: id, sendAsBrokerUid: uid ?? '' }))}
            />
            {preferencesOn && (
              <TopicSelect
                value={journey.topicId}
                disabled={busy || !structureEditable}
                getTopics={contentApi.getTopics}
                onChange={(topicId) => void act(() => api.update({ journeyId: id, topicId }))}
              />
            )}
          </section>

          <section className={EDM_UI.card} aria-label="Steps">
            <h2 className={EDM_UI.heading}>Steps</h2>
            {journey.stepDetails.length === 0 && <p className="text-sm text-gray-400">Add the first email (day 0).</p>}
            <ol className="space-y-3">
              {journey.stepDetails.map((s, i) => (
                <li key={s.stepId} className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-gray-700 px-4 py-3">
                  <div className="space-y-1">
                    <p className="text-sm font-medium text-gray-100">
                      Step {i + 1} · day {s.offsetDays}: {s.subject || <span className="text-gray-400">No subject yet</span>}
                    </p>
                    <p className={`flex items-center gap-1.5 text-xs ${s.tested ? 'text-emerald-300' : 'text-amber-300'}`}>
                      {s.tested ? <CheckCircle2 className="h-3.5 w-3.5" /> : <CircleAlert className="h-3.5 w-3.5" />}
                      {s.tested
                        ? `Test delivered ${s.testDeliveredAt ? formatBrisbaneDateTime(s.testDeliveredAt) : ''}`
                        : 'Needs a delivered test sent after the last edit'}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Link to={`/leads/edm/${s.stepTemplateId}`} className={EDM_UI.secondaryButton}>
                      {structureEditable ? 'Edit email' : 'View email'}
                    </Link>
                    {structureEditable && (
                      <button
                        type="button"
                        className={EDM_UI.secondaryButton}
                        aria-label={`Remove step ${i + 1}`}
                        disabled={busy}
                        onClick={() => void act(() => api.removeStep(id, s.stepId))}
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                </li>
              ))}
            </ol>
            {structureEditable && journey.stepDetails.length < 5 && (
              <div className="flex flex-wrap items-end gap-2">
                <div>
                  <label htmlFor="journey-step-offset" className={EDM_UI.label}>
                    Days after the start
                  </label>
                  <input
                    id="journey-step-offset"
                    type="number"
                    className={EDM_UI.input}
                    value={journey.stepDetails.length === 0 ? '0' : offset}
                    disabled={journey.stepDetails.length === 0}
                    onChange={(e) => setOffset(e.target.value)}
                  />
                </div>
                <button
                  type="button"
                  className={EDM_UI.primaryButton}
                  disabled={busy}
                  onClick={() => void act(() => api.addStep(id, journey.stepDetails.length === 0 ? 0 : Number(offset)))}
                >
                  <Plus className="h-4 w-4" /> Add step
                </button>
              </div>
            )}
          </section>

          {stats && (
            <section className={EDM_UI.card} aria-label="Sent emails">
              <h2 className={EDM_UI.heading}>Emails sent by this journey</h2>
              <p className="text-sm text-gray-300">
                {stats.totals.sent.toLocaleString('en-AU')} sent · {stats.enrolments.active.toLocaleString('en-AU')} people in progress ·{' '}
                {stats.enrolments.completed.toLocaleString('en-AU')} finished · {stats.enrolments.exited.toLocaleString('en-AU')} left early
              </p>
              {stats.children.length > 0 && (
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-700 text-left text-xs uppercase tracking-wider text-gray-400">
                      <th className="py-2">Day</th>
                      <th className="py-2">Step</th>
                      <th className="py-2">Status</th>
                      <th className="py-2">Sent</th>
                    </tr>
                  </thead>
                  <tbody>
                    {stats.children.map((c) => {
                      const stepNo = journey.steps.findIndex((s) => s.stepId === c.stepId) + 1;
                      return (
                        <tr key={c.id} className="border-b border-gray-800">
                          <td className="py-2 text-gray-200">
                            <Link to={`/leads/edm/${c.id}`} className="hover:underline">
                              {c.day ?? '–'}
                            </Link>
                          </td>
                          <td className="py-2 text-gray-300">{stepNo > 0 ? `Step ${stepNo}` : '–'}</td>
                          <td className="py-2 text-gray-300">{statusLabel(c.status)}</td>
                          <td className="py-2 text-gray-300">
                            {c.sentCount.toLocaleString('en-AU')} / {c.totalCount.toLocaleString('en-AU')}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </section>
          )}
        </>
      )}

      <ConfirmDialog
        isOpen={dialog === 'archive'}
        title="Archive this journey?"
        message="No new people join and its scheduled emails are cancelled. People already part-way through stop. This cannot be undone."
        confirmLabel="Archive"
        confirmVariant="danger"
        onCancel={() => setDialog(null)}
        onConfirm={() => {
          setDialog(null);
          void act(() => api.archive(id));
        }}
      />
    </div>
  );
};

export default EdmJourneyPage;

/** Send as: the company, or one eligible team member (loaded on first focus). */
const JourneySenderSelect: React.FC<{
  value: string | null;
  disabled: boolean;
  loadCandidates: () => Promise<{ candidates: EdmSenderCandidate[] }>;
  onChange: (uid: string | null) => void;
}> = ({ value, disabled, loadCandidates, onChange }) => {
  const [candidates, setCandidates] = useState<EdmSenderCandidate[] | null>(null);
  const ensure = () => {
    if (candidates !== null) return;
    setCandidates([]);
    loadCandidates()
      .then((r) => setCandidates([...(r.candidates ?? [])].sort((a, b) => a.displayName.localeCompare(b.displayName))))
      .catch(() => setCandidates([]));
  };
  const known = value === null || (candidates ?? []).some((c) => c.uid === value);
  return (
    <div>
      <label htmlFor="journey-send-as" className={EDM_UI.label}>
        Send as
      </label>
      <select
        id="journey-send-as"
        className={EDM_UI.input}
        value={value ?? ''}
        disabled={disabled}
        onFocus={ensure}
        onMouseDown={ensure}
        onChange={(e) => onChange(e.target.value || null)}
      >
        <option value="">Your company</option>
        {!known && value && <option value={value}>The selected team member</option>}
        {(candidates ?? []).map((c) => (
          <option key={c.uid} value={c.uid}>
            {c.displayName}
          </option>
        ))}
      </select>
    </div>
  );
};
